import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { t, getCurrentLanguage } from '../utils/translations';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';
import { 
  HeartPulse, 
  Megaphone,
  Navigation,
  Link as LinkIcon,
  HandHeart,
  UserCheck,
  ShieldCheck,
  AlertCircle,
  Eye,
  Lock,
  CheckCircle,
  ArrowRight,
  Target,
  Users
} from 'lucide-react';

const SanteSecuritePage = () => {
  const navigate = useNavigate();
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());

  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLanguage(getCurrentLanguage());
    };
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const interventionAxes = [
    {
      number: 1,
      icon: Megaphone,
      titleKey: 'sante.program.axes.axis1.title',
      items: [
        'sante.program.axes.axis1.item1',
        'sante.program.axes.axis1.item2',
        'sante.program.axes.axis1.item3'
      ]
    },
    {
      number: 2,
      icon: Navigation,
      titleKey: 'sante.program.axes.axis2.title',
      items: [
        'sante.program.axes.axis2.item1',
        'sante.program.axes.axis2.item2',
        'sante.program.axes.axis2.item3'
      ]
    },
    {
      number: 3,
      icon: LinkIcon,
      titleKey: 'sante.program.axes.axis3.title',
      items: [
        'sante.program.axes.axis3.item1',
        'sante.program.axes.axis3.item2',
        'sante.program.axes.axis3.item3'
      ]
    },
    {
      number: 4,
      icon: HandHeart,
      titleKey: 'sante.program.axes.axis4.title',
      items: [
        'sante.program.axes.axis4.item1',
        'sante.program.axes.axis4.item2',
        'sante.program.axes.axis4.item3'
      ]
    }
  ];

  const ethicalPrinciples = [
    { icon: UserCheck, key: 'sante.program.ethical.principle1' },
    { icon: ShieldCheck, key: 'sante.program.ethical.principle2' },
    { icon: AlertCircle, key: 'sante.program.ethical.principle3' },
    { icon: Eye, key: 'sante.program.ethical.principle4' },
    { icon: Lock, key: 'sante.program.ethical.principle5' }
  ];

  const expectedResults = [
    'sante.program.results.result1',
    'sante.program.results.result2',
    'sante.program.results.result3',
    'sante.program.results.result4'
  ];

  const socialFundItems = [
    'sante.program.socialFund.item1',
    'sante.program.socialFund.item2',
    'sante.program.socialFund.item3'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section - Compact */}
      <section className="relative h-[280px] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#089556] via-[#0b6f47] to-[#07D1EB]">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 20% 30%, rgba(255,255,255,0.8) 2px, transparent 2px)`,
              backgroundSize: '100px 100px'
            }}></div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center relative z-10 max-w-4xl mx-auto px-4">
              <div className="inline-flex items-center gap-2 mb-3">
                <div className="w-9 h-9 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30">
                  <HeartPulse className="h-4 w-4 text-white" />
                </div>
                <span className="text-xs font-semibold text-white/90 uppercase tracking-wider">
                  {t('sante.program.hero.subtitle')}
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-3xl font-bold text-white mb-3 leading-tight">
                {t('sante.program.hero.title')}
              </h1>
              <p className="text-sm sm:text-base text-white/90 max-w-3xl mx-auto leading-relaxed">
                {t('sante.program.hero.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Context & Objective - Side by Side with Image */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Image Column - 1/2 */}
          <div>
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img 
                src="/projet/sante/Image8.jpeg"
                alt="Programme de santé"
                className="w-full h-[400px] object-cover"
                loading="eager"
              />
            </div>
          </div>
          
          {/* Content Column - 1/2 */}
          <div className="space-y-6">
            {/* Context */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <div className="w-8 h-8 bg-[#089556] rounded-lg flex items-center justify-center">
                  <Target className="w-4 h-4 text-white" />
                </div>
                {t('sante.program.context.title')}
              </h2>
              <p className="text-gray-700 leading-relaxed text-[15px]">
                {t('sante.program.context.content')}
              </p>
            </div>

            {/* Objective */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <div className="w-8 h-8 bg-[#07D1EB] rounded-lg flex items-center justify-center">
                  <Users className="w-4 h-4 text-white" />
                </div>
                {t('sante.program.objective.title')}
              </h2>
              <p className="text-gray-700 leading-relaxed text-[15px]">
                {t('sante.program.objective.content')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Intervention Axes - Compact Grid */}
      <section className="bg-gray-50 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            {t('sante.program.axes.title')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {interventionAxes.map((axis) => {
              const Icon = axis.icon;
              return (
                <div key={axis.number} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 pt-2">
                      {t(axis.titleKey)}
                    </h3>
                  </div>
                  <ul className="space-y-2 ml-16">
                    {axis.items.map((itemKey, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-[#089556] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{t(itemKey)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partnership - With Image */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('sante.program.partnership.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed">
              {t('sante.program.partnership.content')}
            </p>
          </div>
          <div>
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img 
                src="/projet/sante/Image9.jpeg"
                alt="Partenariat"
                className="w-full h-64 object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Ethical Approach - Compact */}
      <section className="bg-gray-50 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            {t('sante.program.ethical.title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ethicalPrinciples.map((principle, idx) => {
              const Icon = principle.icon;
              return (
                <div key={idx} className="bg-white rounded-lg p-4 shadow-sm flex items-start gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <p className="text-sm text-gray-700 leading-snug pt-1">
                    {t(principle.key)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Social Impact & Results - Two Columns */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Social Impact Fund */}
          <div className="bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-xl p-8 text-white">
            <h2 className="text-2xl font-bold mb-4">
              {t('sante.program.socialFund.title')}
            </h2>
            <p className="text-white/90 mb-6 text-sm">
              {t('sante.program.socialFund.intro')}
            </p>
            <ul className="space-y-3">
              {socialFundItems.map((itemKey, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <CheckCircle className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                  <span className="text-white/95">{t(itemKey)}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Expected Results */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {t('sante.program.results.title')}
            </h2>
            <div className="space-y-4">
              {expectedResults.map((resultKey, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                  <CheckCircle className="w-5 h-5 text-[#089556] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-700 leading-snug">
                    {t(resultKey)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vision - With Image */}
      <section className="bg-gray-50 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sante.program.vision.title')}
              </h2>
              <p className="text-gray-700 leading-relaxed">
                {t('sante.program.vision.content')}
              </p>
            </div>
            <div className="order-1 lg:order-2">
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="/projet/sante/Image8.jpg"
                  alt="Vision"
                  className="w-full h-64 object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA - Compact */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="bg-gradient-to-r from-[#089556] to-[#07D1EB] rounded-xl p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-3">
            {currentLanguage === 'fr' 
              ? 'Besoin d\'informations ou d\'accompagnement ?' 
              : 'Need information or support?'}
          </h2>
          <p className="text-white/90 mb-6 max-w-2xl mx-auto">
            {currentLanguage === 'fr'
              ? 'Notre équipe est à votre disposition pour répondre à vos questions.'
              : 'Our team is available to answer your questions.'}
          </p>
          <button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
              navigate(`/${currentLanguage}/contact`);
            }}
            className="inline-flex items-center gap-2 bg-white text-[#089556] px-6 py-3 rounded-lg font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300"
          >
            {t('sante.program.cta.button')}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SanteSecuritePage;
